import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { setLanguagePreference, SUPPORTED_LANGUAGES } from '../i18n';

type LanguageInfo = {
  code: string;
  nativeName: string;
  englishName: string;
};

type LanguageContextType = {
  currentLanguage: string;
  setLanguage: (language: string) => Promise<void>;
  isEnglish: boolean;
  isPortuguese: boolean;
  supportedLanguages: LanguageInfo[];
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export const SUPPORTED_LANGUAGE_INFO: LanguageInfo[] = [
  { code: 'en', nativeName: 'English', englishName: 'English' },
  { code: 'pt-PT', nativeName: 'Português', englishName: 'Portuguese' },
];

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  const setLanguage = useCallback(async (language: string) => {
    await setLanguagePreference(language);
    // i18n.changeLanguage is called inside setLanguagePreference,
    // and the onLanguageChanged listener below will update currentLanguage.
  }, []);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng);
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [i18n]);

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      setLanguage,
      isEnglish: currentLanguage === 'en' || currentLanguage.startsWith('en'),
      isPortuguese: currentLanguage === 'pt-PT' || currentLanguage.startsWith('pt'),
      supportedLanguages: SUPPORTED_LANGUAGE_INFO,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
}